import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { NO_DETAIL_MESSAGE } from "../../api/apiUtils";
import {
  createExport,
  estimateExport,
  ExportEstimateDto,
  ExportFormat,
  ExportJobDto,
  getExport,
  getExportDownloadUrl,
  isAuthFailure,
  isRetryablePollFailure,
  listExports,
} from "../../api/exportsApi";
import ClosePageWarningModal from "../common/ClosePageWarningModal";
import ModalDialog from "../common/ModalDialog";
import { formatBytes, formatTimestampToInternational } from "../common/helpers";
import tailwindClassnames from "../common/TailwindClassnames";
import { MediaAssetQueryType } from "../common/QueryTypes";
import {
  downloadExportMedia,
  MediaDownloadCancelledError,
  MediaDownloadProgress,
  saveBlob,
} from "./exportMediaDownload";
import {
  DEFAULT_EXPORT_FORMAT,
  EXPORT_FORMAT_LABEL,
  EXPORT_FORMATS,
  formatSupportsExtras,
  parseExportFormat,
} from "./exportFormat";

/** How often to re-check a running job. */
const POLL_INTERVAL_MS = 2000;
/**
 * Consecutive poll failures tolerated before giving up and asking the user.
 * A multi-minute export will occasionally hit a dropped connection, and silently
 * recovering from that is better than interrupting; three strikes with backoff
 * (2s, 4s, 8s) distinguishes a blip from the server actually being unreachable.
 */
const MAX_POLL_FAILURES = 3;
/**
 * Completed exports listed for a fresh download link (AKL-892). Enough to reach the
 * export whose link expired last week; few enough that the panel stays a panel.
 */
const RECENT_EXPORTS_SHOWN = 5;

interface ExportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | null;
  projectName?: string;
  /** The live review filters, so the export covers exactly the current working set. */
  filters: MediaAssetQueryType["filters"];
  /** Matching media count from the feed, shown so the user knows the scope. */
  matchingCount: number | null;
}

type ExportOptions = {
  format: ExportFormat;
  /** Wildlife Insights only; sent trimmed, and not at all when blank. */
  imageLocationPrefix: string;
  includeAiPredictions: boolean;
  includeFlatCsv: boolean;
  includeMegadetectorJson: boolean;
  includeDrafts: boolean;
  skipIncompleteDeployments: boolean;
};

/** The options a checkbox toggles — every key of ExportOptions holding a boolean. */
type BooleanOption = {
  [K in keyof ExportOptions]: ExportOptions[K] extends boolean ? K : never;
}[keyof ExportOptions];

const defaultOptions = (): ExportOptions => ({
  format: DEFAULT_EXPORT_FORMAT,
  imageLocationPrefix: "",
  includeAiPredictions: false,
  includeFlatCsv: true,
  includeMegadetectorJson: false,
  includeDrafts: false,
  skipIncompleteDeployments: false,
});

/**
 * Base file name for a downloaded archive, e.g. `mara-carnivore-survey-2026-08-03`.
 *
 * The project name is free text typed by a user, so it is slugged rather than used raw —
 * it ends up as a filename on someone's disk, and spaces, slashes and accents there are at
 * best awkward and at worst rejected by the OS.
 * @param projectName the project's display name, if known
 * @param job the export job, for its creation date
 * @returns a filesystem-safe base name with no extension
 */
const exportBaseName = (
  projectName: string | undefined,
  job: ExportJobDto,
): string => {
  const slug = (projectName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${slug || "akili-export"}-${job.created_at.slice(0, 10)}`;
};

const isTerminal = (job: ExportJobDto | null): boolean =>
  job !== null && ["completed", "failed", "expired"].includes(job.status);

/**
 * Pull a human-readable reason out of whatever the API layer rejected with.
 *
 * The response interceptor normalises 4xx into `{ message, status, data: { detail } }`,
 * where `detail` is the API's already-localized text, so that is preferred. Falls back to
 * `message`, then to null for anything with neither (a network abort has no body at all) —
 * a null simply means the caller shows its generic header with no extra line.
 *
 * `message` is only worth showing when the interceptor had something to put there. On
 * a response whose body carried no `detail` it sets the `NO_DETAIL_MESSAGE` placeholder,
 * which is untranslated and says nothing about what failed, so that one value counts as
 * no detail at all — the caller shows its own wording instead of the word "error".
 *
 * A 422 carries `detail` as FastAPI's list of validation errors instead (AKL-1026 — a
 * Wildlife Insights image location prefix the server refuses). The first entry's `msg` is
 * shown, minus the `Value error, ` that pydantic puts in front of a validator's own
 * sentence, so the user reads what is wrong rather than the generic header alone.
 *
 * Deliberately does NOT inspect the text to detect a particular failure such as a missing
 * project timezone. That message is localized server-side and its wording will change;
 * matching on it would be broken by either. Showing whatever the API said is both simpler
 * and correct for every failure, not just the one that prompted this.
 */
const extractErrorDetail = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null;
  const { data, message } = error as {
    data?: { detail?: unknown };
    message?: unknown;
  };
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: unknown } | undefined;
    if (typeof first?.msg === "string") {
      const msg = first.msg.replace(/^Value error,\s*/, "").trim();
      if (msg) return msg;
    }
  }
  if (
    typeof message === "string" &&
    message.trim() &&
    message !== NO_DETAIL_MESSAGE
  ) {
    return message;
  }
  return null;
};

/**
 * Why status polling stopped. Distinguished because the right message and the right
 * affordance differ: only `transient` is worth offering a retry for, and calling an
 * expired session "a connection problem" sends the user chasing the wrong fix.
 */
type PollFailure = "transient" | "auth" | "permanent";

/**
 * Export the current review selection as a Camtrap DP data package (AKL-554) or as
 * Wildlife Insights bulk-upload files (AKL-1026), whichever format the user picks.
 *
 * Reuses the review page's live filter object rather than offering its own
 * filter controls, so what the user curated in the feed is exactly what they
 * take away. Queues the job, polls until it settles, then offers a download.
 *
 * Status polling tolerates transient failures: it retries with backoff and only
 * surfaces an error (with a "Check again" action that resumes from the same job
 * guid) once MAX_POLL_FAILURES consecutive checks have failed. A dropped status
 * check must never orphan an export that is still running server-side.
 */
const ExportPanel: React.FC<ExportPanelProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  filters,
  matchingCount,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [options, setOptions] = useState<ExportOptions>(defaultOptions);
  const [job, setJob] = useState<ExportJobDto | null>(null);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [failedToStart, setFailedToStart] = useState(false);
  // The API's own reason for a failed start, shown inline. Kept separate from
  // `failedToStart` because a network error has no response body to report (AKL-722).
  const [startFailureDetail, setStartFailureDetail] = useState<string | null>(
    null,
  );
  const [pollFailure, setPollFailure] = useState<PollFailure | null>(null);
  const [estimate, setEstimate] = useState<ExportEstimateDto | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [mediaProgress, setMediaProgress] =
    useState<MediaDownloadProgress | null>(null);
  const [mediaFailedCount, setMediaFailedCount] = useState(0);
  // Completed exports the API still holds, so a fresh data-package link can be minted
  // without re-running the export (AKL-892). Null until the list has been asked for.
  const [recentExports, setRecentExports] = useState<ExportJobDto[] | null>(
    null,
  );
  const [loadingRecent, setLoadingRecent] = useState(false);
  // The recent export whose link is being minted, so only its own button shows busy.
  const [mintingGuid, setMintingGuid] = useState<string | null>(null);
  // Why the last download link could not be minted, shown inline. The interceptor
  // toasts a network failure and redirects on a 5xx, but a 4xx — an export that expired
  // or was removed between listing and clicking — is rejected silently, and a Download
  // button that does nothing is the one outcome this feature must not have. Keyed by
  // the export it is about, so a recent row's failure is never shown under an export
  // built afterwards.
  const [linkError, setLinkError] = useState<{
    guid: string;
    message: string;
  } | null>(null);
  // Identifies the latest link request. Closing the panel bumps it, so a slow answer
  // to an earlier click neither opens a tab under a closed panel nor clears the busy
  // state of a click made after reopening.
  const linkRequestRef = useRef(0);
  // Lets closing the panel stop an in-flight image download. Without it, a cancelled
  // download would keep fetching and eventually hand the user a file they no longer
  // expect.
  const mediaAbortRef = useRef<AbortController | null>(null);
  const mediaDownloading = mediaProgress !== null;
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A ref, not state: incrementing it must not re-run the polling effect (that would
  // cancel the backoff timer it just scheduled). Reset to 0 on every success.
  const failureCountRef = useRef(0);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Poll while the job is in flight. Rescheduled after each response rather than on a
  // fixed interval, so a slow response can't stack up overlapping requests: a successful
  // poll calls setJob, which re-runs this effect and schedules the next tick.
  //
  // A failed poll is retried in place with exponential backoff, up to
  // MAX_POLL_FAILURES. Only then does it stop and set pollFailure, which surfaces an
  // inline message and a Retry action. Giving up on the first error would leave the user
  // watching a spinner forever, and closing the panel to escape would discard the guid
  // of an export that is very likely still running fine on the server.
  //
  // Guarded on `isOpen` as well: without it, closing the panel while a request is in
  // flight lets that response call setJob, which re-runs this effect and schedules
  // another timer — so a closed panel would keep polling until the job reached a
  // terminal state. The clearPoll() in the isOpen effect below cannot prevent that on
  // its own, because it only cancels the timer that exists at the moment of closing.
  useEffect(() => {
    if (!isOpen || !job || isTerminal(job) || pollFailure) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const next = await getExport(job.guid);
        if (cancelled) return;
        failureCountRef.current = 0;
        setJob(next);
      } catch (err) {
        if (cancelled) return;

        // The session is gone (a 401). Re-issue once WITHOUT the bypass so the global
        // interceptor applies the app's standard handling (clears the token, which makes
        // authHeader redirect to login on the next request). Suppressing the toast must
        // not also suppress session expiry — retrying here would burn three attempts and
        // then blame the network for an auth problem. A 403 is not this case (AKL-939):
        // the job was refused to a live session, and takes the permanent path below.
        if (isAuthFailure(err)) {
          void getExport(job.guid, { bypassGlobalErrorHandler: false }).catch(
            () => undefined,
          );
          setPollFailure("auth");
          return;
        }

        // A 4xx other than auth is a definitive answer (e.g. the job was cleaned up);
        // retrying only delays telling the user.
        if (!isRetryablePollFailure(err)) {
          setPollFailure("permanent");
          return;
        }

        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_POLL_FAILURES) {
          setPollFailure("transient");
          return;
        }
        pollRef.current = setTimeout(
          tick,
          POLL_INTERVAL_MS * 2 ** failureCountRef.current,
        );
      }
    };
    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [isOpen, job, pollFailure, clearPoll]);

  // Reset when reopened so a previous run's result isn't mistaken for a new one.
  useEffect(() => {
    if (isOpen) {
      setJob(null);
      setFailedToStart(false);
      setPollFailure(null);
      failureCountRef.current = 0;
      setOptions(defaultOptions());
      setEstimate(null);
      setMediaProgress(null);
      setMediaFailedCount(0);
      setLinkError(null);
    } else {
      clearPoll();
      // Closing the panel abandons any image download in progress.
      mediaAbortRef.current?.abort();
      mediaAbortRef.current = null;
      // ...and disowns any download-link request still in flight: its answer must not
      // open a tab the user no longer expects, and its busy state must not outlive it.
      linkRequestRef.current += 1;
      setMintingGuid(null);
      setDownloading(false);
    }
  }, [isOpen, clearPoll]);

  // Also abort on unmount, which closing the panel does not necessarily cause — the
  // review page can navigate away with the panel still open. The same goes for a
  // download-link request in flight: its answer must not open a tab over whatever
  // page the user navigated to.
  //
  // A layout effect, so the cleanup runs during the commit that unmounts rather than
  // after the browser has painted (Copilot, 2026-09-22 review round). A passive
  // cleanup leaves a window in which the request can resolve against a panel that is
  // already gone. It is a short window and losing the race costs only an unexpected
  // tab, but this cleanup touches nothing except two refs, so closing it is free.
  // The same window on the `isOpen` path is left alone: shutting that one would mean
  // invalidating from the close handlers, which is a larger change than the risk.
  useLayoutEffect(
    () => () => {
      mediaAbortRef.current?.abort();
      linkRequestRef.current += 1;
    },
    [],
  );

  // A page the browser parks in its back/forward cache is neither closed nor
  // unmounted, so neither effect above runs; `pagehide` is the only signal that the
  // user left. A download-link request in flight has to be disowned here too, or
  // pressing Back can open a tab minutes after the click that asked for it — the
  // invariant the two effects above already state. Its own listener rather than
  // ClosePageWarningModal's `onCloseDespiteWarning`, because that one fires only
  // while `warnBeforeClosing` is true, and a link request runs with no download
  // in progress. Clearing the busy state matters on a restore: `mintAndOpenLink`
  // releases it only for a request still wanted, so a disowned one would otherwise
  // leave the button spinning for good.
  //
  // Deliberately does NOT abort the image download (Copilot, 2026-09-21 review).
  // Unlike an upload it holds nothing on the server — no lock, no row, nothing a
  // second user can be blocked by — so leaving costs no one else anything. And an
  // abort here is a no-op whenever the page is really destroyed: it would change
  // only the one case where the page comes back, discarding every image fetched so
  // far for a download the browser was about to finish, which is the opposite of
  // what this ticket is for.
  useEffect(() => {
    const disownLinkRequest = () => {
      linkRequestRef.current += 1;
      setMintingGuid(null);
      setDownloading(false);
    };
    window.addEventListener("pagehide", disownLinkRequest);
    return () => window.removeEventListener("pagehide", disownLinkRequest);
  }, []);

  // `filters` is rebuilt on every render of the review page, so depending on the object
  // itself would fire a request per render. Serialising it gives a value that changes only
  // when the selection actually changes — which is the dependency the effect really has.
  // Filters are discrete selections (no free text), so there is nothing to debounce.
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  // Size the export up when the panel opens, so the user sees the scale before
  // committing rather than discovering it partway through a build. Re-runs when the
  // filters change under an open panel: the estimate gates the in-browser download, so a
  // stale one can offer a path the current selection is too large for. Deliberately not
  // re-run on option changes: the options do not affect which media match, only what is
  // written alongside them.
  useEffect(() => {
    if (!isOpen || projectId === null) return;

    let cancelled = false;
    setEstimating(true);
    // Parsed back rather than closing over `filters`, so the effect depends on the
    // serialised value alone and the dependency list stays honest.
    estimateExport({
      project_id: projectId,
      filters: JSON.parse(filtersKey) as MediaAssetQueryType["filters"],
    })
      .then((result) => {
        if (!cancelled) setEstimate(result);
      })
      .catch(() => {
        // Deliberately quiet: the estimate is a convenience, and the panel is still
        // fully usable without it. Errors are already logged by the API client.
      })
      .finally(() => {
        if (!cancelled) setEstimating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, filtersKey]);

  // Recent completed exports, fetched when the panel opens. The API keeps a finished
  // export for weeks but its download link for a day, and a browser download that
  // fails after the link has expired cannot be resumed — this is how the user gets a
  // fresh link without building the export again (AKL-892). Quiet on failure for the
  // same reason the estimate is: nobody asked for it, and the panel works without it.
  useEffect(() => {
    if (!isOpen || projectId === null) {
      // Also clears a spinner left behind by a close mid-request, whose response the
      // cleanup below has told to ignore itself.
      setRecentExports(null);
      setLoadingRecent(false);
      return;
    }

    let cancelled = false;
    setLoadingRecent(true);
    listExports(projectId)
      .then((result) => {
        if (cancelled) return;
        setRecentExports(
          result.exports
            .filter((recent) => recent.status === "completed")
            .slice(0, RECENT_EXPORTS_SHOWN),
        );
      })
      .catch(() => {
        if (!cancelled) setRecentExports([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecent(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  /**
   * Fetch this export's photos and save them as a zip.
   *
   * Bytes come straight from blob storage to the browser; the API only issues the signed
   * URLs. Failures are surfaced as a count rather than aborting: a package that is short a
   * few unreadable images is still worth having, and the user can re-run to pick them up.
   */
  const handleDownloadImages = async () => {
    if (job === null || mediaDownloading) return;

    const controller = new AbortController();
    mediaAbortRef.current = controller;
    setMediaFailedCount(0);
    setMediaProgress({
      done: 0,
      total: 0,
      paging: true,
      failed: 0,
      waitingForNetwork: false,
    });

    try {
      const result = await downloadExportMedia(
        job.guid,
        `${exportBaseName(projectName, job)}-images.zip`,
        setMediaProgress,
        controller.signal,
      );
      saveBlob(result.blob, result.fileName);
      setMediaFailedCount(result.failed.length);
    } catch (err) {
      // A cancel is the user's own doing, so it needs no message. Anything else has
      // already been toasted by the axios interceptor or logged by the API client.
      if (!(err instanceof MediaDownloadCancelledError)) {
        console.error(err);
      }
    } finally {
      // Only the download that still owns the panel clears the progress line. One
      // cancelled by a close settles later, possibly under a newer download started
      // after reopening — its progress must not be wiped, nor its leave-page guard
      // dropped. The reopen effect has already cleared the closed one's state.
      if (mediaAbortRef.current === controller) {
        mediaAbortRef.current = null;
        setMediaProgress(null);
      }
    }
  };

  /**
   * Resume polling the existing job after a run of failed status checks.
   * Keeps the same job guid, so an export that kept running server-side is picked back
   * up rather than orphaned.
   */
  const handleRetryStatus = () => {
    failureCountRef.current = 0;
    // Clearing the failure re-runs the polling effect (it's a dependency).
    setPollFailure(null);
  };

  /**
   * Forget the last refusal: a job whose build failed, or a start the API turned down.
   * Its message was about the options as they were when Start export was pressed, so it
   * no longer applies once they change or a new attempt begins (AKL-1085). A job in any
   * other state is left alone; the options are not on show for one.
   */
  const clearRefusal = () => {
    setJob((current) => (current?.status === "failed" ? null : current));
    setFailedToStart(false);
    setStartFailureDetail(null);
  };

  /**
   * Apply an option change and clear the refusal it may answer (AKL-1085).
   * @param change builds the next options from the current ones
   */
  const changeOptions = (change: (current: ExportOptions) => ExportOptions) => {
    setOptions(change);
    clearRefusal();
  };

  const handleStart = async () => {
    if (projectId === null) return;
    setStarting(true);
    // Also drops a failed job, whose reason the message would otherwise keep showing
    // over a retry refused at the start.
    clearRefusal();
    setPollFailure(null);
    failureCountRef.current = 0;
    // The three extras stay in state while Wildlife Insights is chosen, so switching back
    // restores them, but are sent as false: that writer ignores them, and a stale tick
    // would only earn the job a warning about an option the user cannot see (AKL-1026).
    const extras = formatSupportsExtras(options.format);
    const prefix = options.imageLocationPrefix.trim();
    try {
      const created = await createExport({
        project_id: projectId,
        filters,
        format: options.format,
        include_ai_predictions: extras && options.includeAiPredictions,
        include_flat_csv: extras && options.includeFlatCsv,
        include_megadetector_json: extras && options.includeMegadetectorJson,
        include_drafts: options.includeDrafts,
        skip_incomplete_deployments: options.skipIncompleteDeployments,
        ...(options.format === "wildlife_insights" && prefix
          ? { image_location_prefix: prefix }
          : {}),
      });
      setJob(created);
    } catch (error: unknown) {
      // The response interceptor normalises 4xx into a rejected object carrying the API's
      // message but does NOT toast it, so without surfacing it here the reason is lost
      // entirely and the panel just says "could not be completed". That matters most for a
      // project with no timezone configured (AKL-722): the API explains exactly what to
      // set, and the user needs to still be able to read it.
      setFailedToStart(true);
      setStartFailureDetail(extractErrorDetail(error));
    } finally {
      setStarting(false);
    }
  };

  /**
   * What to tell the user when a download link could not be minted.
   *
   * A 4xx carries the API's own, already-localized reason (an expired or removed
   * export), which is the most useful thing to show. Anything else — a network failure
   * the interceptor has already toasted, a 5xx it has already redirected on, or a
   * response with no detail — gets the generic line rather than an axios message.
   * @param error whatever the API layer rejected with
   * @returns the sentence to show inline
   */
  const linkFailureMessage = (error: unknown): string => {
    const status = (error as { status?: unknown } | null)?.status;
    const detail =
      typeof status === "number" && status >= 400 && status < 500
        ? extractErrorDetail(error)
        : null;
    return detail ?? t("The download link could not be created.");
  };

  /**
   * Mint a fresh, time-limited link for a finished export and open it.
   *
   * Shared by the export just built and by the recent-exports list: the API signs a new
   * link on every call, so an export whose earlier link has expired is one click from a
   * working download rather than a re-run (AKL-892). The request is owned by the panel
   * as it is now: if the panel closes before the answer arrives, nothing is opened and
   * nothing is cleared — the close already did that.
   * @param guid the export job guid
   * @param setBusy shows and hides the busy state of whichever button was pressed
   */
  const mintAndOpenLink = async (
    guid: string,
    setBusy: (busy: boolean) => void,
  ) => {
    const request = ++linkRequestRef.current;
    const stillWanted = () => request === linkRequestRef.current;
    setBusy(true);
    setLinkError(null);
    try {
      const { download_url } = await getExportDownloadUrl(guid);
      if (!stillWanted()) return;
      window.open(download_url, "_blank", "noopener,noreferrer");
    } catch (error: unknown) {
      if (!stillWanted()) return;
      setLinkError({ guid, message: linkFailureMessage(error) });
    } finally {
      if (stillWanted()) setBusy(false);
    }
  };

  const handleDownload = () => {
    if (!job) return;
    void mintAndOpenLink(job.guid, setDownloading);
  };

  const handleDownloadRecent = (guid: string) => {
    if (mintingGuid !== null) return;
    void mintAndOpenLink(guid, (busy) => setMintingGuid(busy ? guid : null));
  };

  const toggle = (key: BooleanOption) => () =>
    changeOptions((current) => ({ ...current, [key]: !current[key] }));

  const showExtras = formatSupportsExtras(options.format);

  // Nothing started yet, or the last export was refused. A refusal such as a deployment
  // with no location arrives as a failed job, and its message names an option to change,
  // so the options stay on show beside it rather than only while there is no job
  // (AKL-1085). One flag for every section it gates, so clearing a refusal remounts
  // nothing: the box just clicked keeps its focus and nothing below it jumps.
  const settingUp = job === null || job.status === "failed";

  if (!isOpen) return null;

  // Excludes pollFailure so a stalled status check shows the recovery UI, not a spinner
  // that can never resolve.
  const running = job !== null && !isTerminal(job) && pollFailure === null;

  return (
    <>
      {/* Everything fetched so far lives in memory until the zip is built, so a reload
          or a closed tab throws the whole download away. Same guard the upload uses;
          the browser shows its own wording, the message is for older ones. It renders
          nothing, so it sits beside the dialog rather than inside it. */}
      <ClosePageWarningModal
        warnBeforeClosing={mediaDownloading}
        warningMessage={t(
          "An image download is still running. Leaving this page will discard it.",
        )}
      />
      {/* Escape is refused only while the export is being started (`starting`), the one
          state in which the Cancel button is disabled too. An image download in progress
          is deliberately NOT `busy`: closing abandons it, as the reopen effect intends. */}
      <ModalDialog
        onClose={onClose}
        busy={starting}
        labelledBy={titleId}
        overlayClassName="z-50"
        panelClassName="max-w-xl"
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 id={titleId} className={tailwindClassnames.modalTitle}>
            {t("Export data")}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          <p className={`${tailwindClassnames.helperText} mt-2`}>
            {options.format === "wildlife_insights"
              ? t(
                  "Exports the images currently matching your filters as Wildlife Insights bulk-upload files.",
                )
              : t(
                  "Exports the images currently matching your filters as a Camtrap DP data package, readable in R and Excel.",
                )}
          </p>

          {projectName && (
            <p className={`${tailwindClassnames.basicLabel} mt-3`}>
              {projectName}
              {matchingCount !== null && (
                <span className={tailwindClassnames.helperText}>
                  {" — "}
                  {t("{{total}} images match your filters", {
                    total: matchingCount,
                  })}
                  {/* Server-side estimate: the count above comes from the feed, the
                      size from media_asset.file_size_bytes. Rendered only when the
                      server had something to measure — never a guessed number. */}
                  {estimate?.estimated_bytes != null &&
                    ` · ${
                      estimate.is_estimate
                        ? t("about {{size}} of images", {
                            size: formatBytes(estimate.estimated_bytes),
                          })
                        : t("{{size}} of images", {
                            size: formatBytes(estimate.estimated_bytes),
                          })
                    }`}
                  {estimating && ` · ${t("sizing…")}`}
                </span>
              )}
            </p>
          )}

          {/* --- options ------------------------------------------------- */}
          {/* Both fieldsets are locked while the export is being started: the request
              has already captured these values, and a change made now would leave the
              intro describing a different export from the one being built. */}
          {settingUp && (
            <fieldset
              className="mt-4"
              data-testid="export-format"
              disabled={starting}
            >
              <legend className={tailwindClassnames.basicSubHeading}>
                {t("Format")}
              </legend>
              {EXPORT_FORMATS.map((candidate) => {
                const id = `export-format-${candidate}`;
                return (
                  <div key={candidate} className="flex items-start gap-2 mt-2">
                    <input
                      id={id}
                      type="radio"
                      name="export-format"
                      className={tailwindClassnames.checkBox}
                      checked={options.format === candidate}
                      onChange={() =>
                        changeOptions((current) => ({
                          ...current,
                          format: candidate,
                        }))
                      }
                      data-testid={id}
                    />
                    <label
                      htmlFor={id}
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t(EXPORT_FORMAT_LABEL[candidate])}
                    </label>
                  </div>
                );
              })}

              {options.format === "wildlife_insights" && (
                <div className="mt-3">
                  <label
                    htmlFor="export-image-location-prefix"
                    className={tailwindClassnames.textInputLabel}
                  >
                    {t("Image location prefix (optional)")}
                  </label>
                  <input
                    id="export-image-location-prefix"
                    type="text"
                    className={tailwindClassnames.detailsPageTextInput}
                    value={options.imageLocationPrefix}
                    onChange={(event) => {
                      const { value } = event.target;
                      changeOptions((current) => ({
                        ...current,
                        imageLocationPrefix: value,
                      }));
                    }}
                    placeholder="gs://my-bucket/my-project"
                    aria-describedby="export-image-location-prefix-help"
                    data-testid="export-image-location-prefix"
                  />
                  <p
                    id="export-image-location-prefix-help"
                    className={`${tailwindClassnames.helperText} mt-1`}
                  >
                    {t(
                      "Where you will upload the images for Wildlife Insights. It is added to the start of every image location. Leave it empty to fill it in later.",
                    )}
                  </p>
                </div>
              )}
            </fieldset>
          )}

          {settingUp && (
            <fieldset className="mt-4" disabled={starting}>
              <legend className={tailwindClassnames.basicSubHeading}>
                {t("Include")}
              </legend>

              {showExtras && (
                <div className="flex items-start gap-2 mt-2">
                  <input
                    id="export-flat-csv"
                    type="checkbox"
                    className={tailwindClassnames.checkBox}
                    checked={options.includeFlatCsv}
                    onChange={toggle("includeFlatCsv")}
                  />
                  <label
                    htmlFor="export-flat-csv"
                    className={tailwindClassnames.checkBoxLabel}
                  >
                    {t("A single combined spreadsheet (for Excel or GIS)")}
                  </label>
                </div>
              )}

              {showExtras && (
                <>
                  <div className="flex items-start gap-2 mt-2">
                    <input
                      id="export-ai"
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={options.includeAiPredictions}
                      onChange={toggle("includeAiPredictions")}
                    />
                    <label
                      htmlFor="export-ai"
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t("AI predictions for images not yet reviewed")}
                    </label>
                  </div>

                  <div className="flex items-start gap-2 mt-2">
                    <input
                      id="export-megadetector"
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={options.includeMegadetectorJson}
                      onChange={toggle("includeMegadetectorJson")}
                    />
                    <label
                      htmlFor="export-megadetector"
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t("MegaDetector / AddaxAI results file")}
                    </label>
                  </div>
                </>
              )}

              <div className="flex items-start gap-2 mt-2">
                <input
                  id="export-drafts"
                  type="checkbox"
                  className={tailwindClassnames.checkBox}
                  checked={options.includeDrafts}
                  onChange={toggle("includeDrafts")}
                />
                <label
                  htmlFor="export-drafts"
                  className={tailwindClassnames.checkBoxLabel}
                >
                  {t("Observations in media not yet marked 'Reviewed'")}
                </label>
              </div>

              <div className="flex items-start gap-2 mt-2">
                <input
                  id="export-skip-incomplete"
                  type="checkbox"
                  className={tailwindClassnames.checkBox}
                  checked={options.skipIncompleteDeployments}
                  onChange={toggle("skipIncompleteDeployments")}
                />
                <label
                  htmlFor="export-skip-incomplete"
                  className={tailwindClassnames.checkBoxLabel}
                >
                  {t("Skip deployments that have no location set")}
                </label>
              </div>
            </fieldset>
          )}

          {/* --- recent exports ------------------------------------------- */}
          {/* Only while nothing has been started (or the last export was refused), and
              only when there is something to list: a "none yet" line on every first
              export would be noise, and the section is supplementary to the options
              above it. */}
          {settingUp && (loadingRecent || (recentExports?.length ?? 0) > 0) && (
            <section
              className="mt-4 border-t border-off-white-dark pt-3 dark:border-storm-blue-light"
              aria-busy={loadingRecent}
              data-testid="recent-exports"
            >
              <h3 className={tailwindClassnames.basicSubHeading}>
                {t("Recent exports")}
              </h3>
              {loadingRecent ? (
                <div className="mt-2">
                  <BeatLoader size={8} color={tailwindClassnames.stormBlue} />
                </div>
              ) : (
                <ul className="mt-2 list-none space-y-2">
                  {recentExports?.map((recent) => {
                    // Date and time, not the date alone: an export is often re-run
                    // the same day, and two rows reading "2026-09-16" would be
                    // indistinguishable — to the eye and to a screen reader, which
                    // gets the same string as the button's accessible name.
                    const date = formatTimestampToInternational(
                      new Date(recent.created_at).getTime() / 1000,
                    );
                    const minting = mintingGuid === recent.guid;
                    return (
                      <li
                        key={recent.guid}
                        className="flex items-center justify-between gap-3"
                      >
                        <span className={tailwindClassnames.helperText}>
                          <span>{date}</span>
                          {" · "}
                          <span>
                            {t(
                              EXPORT_FORMAT_LABEL[
                                parseExportFormat(recent.format)
                              ],
                            )}
                          </span>
                          {" · "}
                          <span>
                            {t("{{total}} images", {
                              total: recent.media_count ?? 0,
                            })}
                          </span>
                          {recent.result_size_bytes !== null && (
                            <>
                              {" · "}
                              <span>
                                {formatBytes(recent.result_size_bytes)}
                              </span>
                            </>
                          )}
                        </span>
                        <button
                          type="button"
                          className={`relative ${tailwindClassnames.btnSecondary}`}
                          onClick={() => handleDownloadRecent(recent.guid)}
                          disabled={mintingGuid !== null}
                          aria-label={t("Download the export from {{date}}", {
                            date,
                          })}
                          data-testid="recent-export-download"
                        >
                          <span className={minting ? "invisible" : ""}>
                            {t("Download")}
                          </span>
                          {minting && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <BeatLoader
                                color="currentColor"
                                loading={true}
                                size={10}
                                margin={5}
                              />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {linkError &&
                recentExports?.some(
                  (recent) => recent.guid === linkError.guid,
                ) && (
                  <p
                    className={`${tailwindClassnames.warningLabel} mt-2`}
                    aria-live="assertive"
                    data-testid="download-link-error"
                  >
                    {linkError.message}
                  </p>
                )}
            </section>
          )}

          {/* --- progress ------------------------------------------------ */}
          {running && (
            <div className="mt-4 flex items-center gap-3" aria-live="polite">
              {/* Palette token, not a raw hex: #3b82f6 was stock Tailwind blue-500 and
                  appeared nowhere else in the app. */}
              <BeatLoader size={8} color={tailwindClassnames.stormBlue} />
              <span className={tailwindClassnames.basicLabel}>
                {job?.status === "queued"
                  ? t("Export queued...")
                  : t("Building your export...")}
              </span>
            </div>
          )}

          {/* --- result -------------------------------------------------- */}
          {job?.status === "completed" && (
            <div className="mt-4" aria-live="polite">
              <p className={tailwindClassnames.basicLabel}>
                {t("Your export is ready.")}
              </p>
              <ul className={`${tailwindClassnames.helperText} mt-1 list-none`}>
                <li data-testid="export-result-format">
                  {t(EXPORT_FORMAT_LABEL[parseExportFormat(job.format)])}
                </li>
                <li>
                  {t("{{total}} deployments", {
                    total: job.deployment_count ?? 0,
                  })}
                </li>
                <li>
                  {t("{{total}} images", { total: job.media_count ?? 0 })}
                </li>
                <li>
                  {t("{{total}} observations", {
                    total: job.observation_count ?? 0,
                  })}
                </li>
                {job.result_size_bytes !== null && (
                  <li>{formatBytes(job.result_size_bytes)}</li>
                )}
              </ul>

              {job.excluded_human_media_count ? (
                <p className={`${tailwindClassnames.warningLabel} mt-2`}>
                  {t(
                    "{{total}} images containing people were left out of this export.",
                    { total: job.excluded_human_media_count },
                  )}
                </p>
              ) : null}

              {job.warnings.map((warning) => (
                <p
                  key={warning}
                  className={`${tailwindClassnames.warningLabel} mt-2`}
                >
                  {warning}
                </p>
              ))}

              {linkError?.guid === job.guid && (
                <p
                  className={`${tailwindClassnames.warningLabel} mt-2`}
                  aria-live="assertive"
                  data-testid="download-link-error"
                >
                  {linkError.message}
                </p>
              )}

              {/* --- images -------------------------------------------- */}
              {/* Photos are fetched separately, straight from storage to the browser —
                  they never pass through the API. Below the server's threshold that
                  happens here; above it the user needs the desktop transfer tool, which
                  has no in-memory ceiling. */}
              {/* Gated on a resolved estimate, not just a truthy one. While it is
                  still loading — or if it failed, which is deliberately silent —
                  the app does not know whether the browser can cope, and saying
                  "too many images" would be asserting something it has not
                  measured. Showing nothing is the honest option; the package's
                  README still explains how to fetch the photos. */}
              {(job.media_count ?? 0) > 0 && estimate !== null && (
                <div className="mt-4 border-t border-off-white-dark pt-3 dark:border-storm-blue-light">
                  {estimate.browser_download_supported ? (
                    <>
                      <button
                        className={tailwindClassnames.btnPrimary}
                        onClick={handleDownloadImages}
                        disabled={mediaDownloading}
                      >
                        {t("Download images")}
                      </button>
                      <p className={`${tailwindClassnames.helperText} mt-2`}>
                        {t(
                          "Images are saved separately. Unzip them next to your data files.",
                        )}
                      </p>
                    </>
                  ) : (
                    <p className={tailwindClassnames.helperText}>
                      {t(
                        "Too many images to download here. Use Akili Transfer to get them.",
                      )}{" "}
                      {/* The "I don't have the app yet" path (AKL-803). The deep link for
                          people who already have it is AKL-726. */}
                      <Link
                        to="/transfer"
                        className="font-semibold text-storm-blue hover:underline dark:text-sky-blue-light"
                        data-testid="export-get-akili-transfer"
                      >
                        {t("Get Akili Transfer")}
                      </Link>
                    </p>
                  )}

                  {mediaProgress && (
                    <div className="mt-2" aria-live="polite">
                      <div className="flex items-center gap-3">
                        <BeatLoader
                          size={8}
                          color={tailwindClassnames.stormBlue}
                        />
                        <span className={tailwindClassnames.basicLabel}>
                          {/* The failed count appears the moment something fails, not
                              only at the end: a run that is losing images should look
                              like one while there is still time to stop it. */}
                          {mediaProgress.failed > 0
                            ? t(
                                "Downloading images… {{done}} of {{total}} ({{failed}} failed)",
                                {
                                  done: mediaProgress.done,
                                  total: mediaProgress.total,
                                  failed: mediaProgress.failed,
                                },
                              )
                            : t("Downloading images… {{done}} of {{total}}", {
                                done: mediaProgress.done,
                                total: mediaProgress.total,
                              })}
                        </span>
                      </div>
                      {mediaProgress.waitingForNetwork && (
                        <p className={`${tailwindClassnames.helperText} mt-1`}>
                          {t("Waiting for the network to come back…")}
                        </p>
                      )}
                    </div>
                  )}

                  {mediaFailedCount > 0 && (
                    <p
                      className={`${tailwindClassnames.warningLabel} mt-2`}
                      aria-live="polite"
                    >
                      {t("{{total}} images could not be downloaded.", {
                        total: mediaFailedCount,
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {(job?.status === "failed" || failedToStart) && (
            <div className="mt-4" aria-live="assertive">
              <p className={tailwindClassnames.warningHeader}>
                {t("The export could not be completed.")}
              </p>
              {(job?.error ?? startFailureDetail) && (
                <p className={`${tailwindClassnames.helperText} mt-1`}>
                  {job?.error ?? startFailureDetail}
                </p>
              )}
            </div>
          )}

          {pollFailure === "transient" && (
            <div className="mt-4" aria-live="assertive">
              <p className={tailwindClassnames.warningHeader}>
                {t("Lost contact with the server while checking your export.")}
              </p>
              <p className={`${tailwindClassnames.helperText} mt-1`}>
                {t(
                  "Your export is probably still running. Use Check again to pick it back up.",
                )}
              </p>
            </div>
          )}

          {pollFailure === "auth" && (
            <div className="mt-4" aria-live="assertive">
              <p className={tailwindClassnames.warningHeader}>
                {t("Your session has expired. Please sign in again.")}
              </p>
              <p className={`${tailwindClassnames.helperText} mt-1`}>
                {t(
                  "Your export is probably still running and will be waiting when you return.",
                )}
              </p>
            </div>
          )}

          {pollFailure === "permanent" && (
            <div className="mt-4" aria-live="assertive">
              <p className={tailwindClassnames.warningHeader}>
                {t("The export could not be completed.")}
              </p>
            </div>
          )}

          {job?.status === "expired" && (
            <p className={`${tailwindClassnames.warningLabel} mt-4`}>
              {t("This export has expired. Please run it again.")}
            </p>
          )}
        </div>

        {/* --- actions ------------------------------------------------- */}
        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onClose}
            disabled={starting}
          >
            {job?.status === "completed" ? t("Close") : t("Cancel")}
          </button>

          {job?.status === "completed" ? (
            <button
              type="button"
              className={`relative ${tailwindClassnames.btnPrimary}`}
              onClick={handleDownload}
              disabled={downloading}
            >
              {/* Label stays in layout while downloading so the button keeps
                  its width; currentColor spinner reads on the primary fill in
                  both themes. */}
              <span className={downloading ? "invisible" : ""}>
                {t("Download")}
              </span>
              {downloading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <BeatLoader
                    color="currentColor"
                    loading={true}
                    size={10}
                    margin={5}
                  />
                </span>
              )}
            </button>
          ) : pollFailure === "transient" ? (
            // Offered only for transient failures. Retrying an expired session or a
            // definitively-gone job cannot succeed, so a button there would just invite
            // the user to keep pressing it.
            <button
              type="button"
              className={tailwindClassnames.btnPrimary}
              onClick={handleRetryStatus}
            >
              {t("Check again")}
            </button>
          ) : pollFailure ? null : (
            <button
              type="button"
              className={`relative ${tailwindClassnames.btnPrimary}`}
              onClick={handleStart}
              // Not while a recent row's link is being minted: starting an export hides
              // that list, and a failure arriving afterwards would have nowhere to show.
              disabled={
                projectId === null ||
                starting ||
                running ||
                mintingGuid !== null
              }
            >
              {/* Label stays in layout while starting so the button keeps its
                  width; currentColor spinner reads on the primary fill in
                  both themes. */}
              <span className={starting ? "invisible" : ""}>
                {t("Start export")}
              </span>
              {starting && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <BeatLoader
                    color="currentColor"
                    loading={true}
                    size={10}
                    margin={5}
                  />
                </span>
              )}
            </button>
          )}
        </div>
      </ModalDialog>
    </>
  );
};

export default ExportPanel;
